import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, CalendarDays } from "lucide-react";
import { FeaturedExperiences } from "@/components/site/FeaturedExperiences";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const fmtDates = (o) => {
  if (!o.start_date && !o.end_date) return null;
  const f = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
  return [f(o.start_date), f(o.end_date)].filter(Boolean).join(" – ");
};

export const OffersShowcase = ({ onRequest }) => {
  const [offers, setOffers] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const run = async () => {
      try {
        const content = await axios.get(`${API}/site-content`).catch(() => ({ data: {} }));
        const limit = content.data?.featured_offers_count || undefined;
        const feat = await axios.get(`${API}/offers`, { params: { featured: true, limit } });
        if (feat.data.length > 0) return setOffers(feat.data);
        const all = await axios.get(`${API}/offers`, { params: { limit } });
        setOffers(all.data);
      } catch {
        setOffers([]);
      }
    };
    run();
  }, []);

  if (offers === null) return <section id="featured" className="py-20" />;
  // Graceful fallback to the curated static cards when there are no live offers yet.
  if (offers.length === 0) return <FeaturedExperiences onSelectInterest={(interest) => onRequest({ interest })} />;

  const handleCta = (o) => {
    trackEvent("cta_click", { location: "offers", cta: "view_trip", offer_id: o.id });
    navigate(`/offers/${o.slug || o.id}`);
  };

  return (
    <section id="featured" data-testid="offers-showcase" className="py-20 md:py-32 bg-[#EAE4DA]/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-16">
          <p className="uppercase text-sm tracking-[0.2em] font-semibold text-terracotta mb-4">Current offers</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-ink">
            Featured Travel Experiences
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {offers.map((o, i) => {
            const dates = fmtDates(o);
            return (
              <motion.article
                key={o.id}
                data-testid={`offer-public-${o.id}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => handleCta(o)}
                className="group overflow-hidden rounded-3xl bg-white border border-bone shadow-sm hover:shadow-xl transition-shadow duration-500 flex flex-col cursor-pointer"
              >
                <div className="relative overflow-hidden">
                  {o.image_url
                    ? <img src={resolveImg(o.image_url)} alt={o.title} className="w-full h-[240px] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    : <div className="w-full h-[240px] bg-bone" />}
                  <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-ink text-[11px] font-semibold px-2.5 py-1 rounded-full">{o.status}</span>
                  <span className="absolute top-3 right-3 bg-terracotta text-white text-[11px] font-semibold px-2.5 py-1 rounded-full">{o.travel_type}</span>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-serif text-2xl font-semibold text-ink">{o.title}</h3>
                  {dates && <p className="text-sm text-ink-soft flex items-center gap-1.5 mt-2"><CalendarDays size={14} /> {dates}</p>}
                  {o.destinations?.length > 0 && <p className="text-sm text-ink-soft flex items-center gap-1.5 mt-1"><MapPin size={14} /> {o.destinations.join(" • ")}</p>}
                  {o.short_description && <p className="text-base leading-relaxed text-ink-soft mt-3 flex-1">{o.short_description}</p>}
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-bone">
                    {o.starting_price ? <span className="text-ink font-semibold">From {o.starting_price}</span> : <span />}
                    <Button
                      data-testid={`offer-cta-${o.id}`}
                      onClick={(e) => { e.stopPropagation(); handleCta(o); }}
                      variant="ghost"
                      className="rounded-full px-0 text-terracotta hover:text-terracotta-hover hover:bg-transparent font-semibold group/btn"
                    >
                      View Trip
                      <ArrowRight size={18} className="ml-1 transition-transform group-hover/btn:translate-x-1" />
                    </Button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
